import { motion } from "framer-motion";
import { HERO } from "../../data/content";
import { maskParent, maskChild } from "./Reveal";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-[72px] flex flex-col overflow-hidden"
    >
      <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 flex-1 flex flex-col">
        {/* Top brand row — reflects the firm on landing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="pt-8 md:pt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-foreground/15 pb-6"
          data-testid="hero-brand"
        >
          <div>
            <p className="font-serif-display text-2xl md:text-3xl tracking-tight leading-none">
              Ranvah Law Chambers
            </p>
            <p className="mt-2 font-mono-accent text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Mansie Himanshu Ranvah · Advocate
            </p>
          </div>
          <nav className="flex flex-wrap gap-2" data-testid="hero-nav-tabs">
            {[
              { label: "Services", href: "#services" },
              { label: "Why POSH", href: "#manifesto" },
              { label: "Expertise", href: "#expertise" },
              { label: "About", href: "#about" },
              { label: "Contact", href: "#contact" },
            ].map((tab) => (
              <a
                key={tab.label}
                href={tab.href}
                data-testid={`hero-tab-${tab.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="border border-foreground/25 px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300"
              >
                {tab.label}
              </a>
            ))}
          </nav>
        </motion.div>

        {/* Heading + compliance image */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-10 md:py-14">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.h1
              variants={maskParent}
              initial="hidden"
              animate="show"
              className="font-serif-display font-medium tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[4.6vw] leading-[0.98]"
              data-testid="hero-heading"
            >
              {HERO.lines.map((line, i) => (
                <span key={i} className="line-mask">
                  <motion.span variants={maskChild} className="block">
                    {line}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 1 }}
              className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground"
              data-testid="hero-intro"
            >
              {HERO.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 1 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 text-xs uppercase tracking-[0.18em] font-mono-accent hover:bg-accent transition-colors duration-300"
              >
                Book a Consultation
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  &rarr;
                </span>
              </a>
              <a
                href="#services"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center gap-2 px-2 py-4 text-xs uppercase tracking-[0.18em] font-mono-accent text-foreground/80"
              >
                <span className="border-b border-foreground/40 group-hover:border-foreground transition-colors duration-300 pb-1">
                  Explore Services
                </span>
              </a>
            </motion.div>
          </div>

          {/* Compliance / policy image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
            data-testid="hero-image-wrap"
          >
            <div className="relative overflow-hidden border border-foreground/15">
              <img
                src={HERO.image}
                alt="POSH policy and workplace compliance documentation"
                className="w-full h-[280px] sm:h-[360px] lg:h-[460px] object-cover grayscale-[20%]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-black/5" />
              <div className="absolute bottom-0 left-0 right-0 bg-primary/85 backdrop-blur-sm px-5 py-3">
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-primary-foreground">
                  {HERO.imageCaption}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
